/**
 * AI Assistant Drawer
 *
 * Main drawer component for the AI Assistant.
 * Contains history, chat area, and input.
 * Action buttons (export, new, close) are in the History header.
 * Features resizable width with localStorage persistence.
 */

import React, { useState, useCallback, useEffect, useRef } from 'react';
import { observer } from 'mobx-react-lite';
import { useReactToPrint } from 'react-to-print';
import {
  Drawer,
  Box,
  Divider,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import { useRootStore } from '../../store/contexts';
import { AiAssistantHistory } from './AiAssistantHistory';
import { AiAssistantChat } from './AiAssistantChat';
import { AiAssistantInput } from './AiAssistantInput';

const DRAWER_WIDTH_DEFAULT = 520;
const DRAWER_WIDTH_MIN = 360;
const DRAWER_WIDTH_MAX = 800;
const DRAWER_WIDTH_MOBILE = '100%';
const STORAGE_KEY = 'aiAssistantDrawerWidth';

export const AiAssistantDrawer: React.FC = observer(() => {
  const rootStore = useRootStore();
  const { aiAssistantStore, prefStore } = rootStore;
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const contentTop = prefStore.getCurrentContentTop();
  const printRef = useRef<HTMLDivElement>(null);

  const handlePrint = useReactToPrint({
    content: () => printRef.current,
    documentTitle: 'AI Assistant - Conversación',
  });

  // Load width from localStorage or use default
  const [drawerWidth, setDrawerWidth] = useState(() => {
    if (typeof window !== 'undefined') {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const parsed = parseInt(stored, 10);
        if (!isNaN(parsed) && parsed >= DRAWER_WIDTH_MIN && parsed <= DRAWER_WIDTH_MAX) {
          return parsed;
        }
      }
    }
    return DRAWER_WIDTH_DEFAULT;
  });

  // Save width to localStorage when it changes
  useEffect(() => {
    if (!isMobile) {
      localStorage.setItem(STORAGE_KEY, String(drawerWidth));
    }
  }, [drawerWidth, isMobile]);

  const handleMouseDown = useCallback(() => {
    const handleMouseMove = (e: MouseEvent) => {
      const newWidth = document.body.offsetWidth - e.clientX;
      if (newWidth >= DRAWER_WIDTH_MIN && newWidth <= DRAWER_WIDTH_MAX) {
        setDrawerWidth(newWidth);
      }
    };

    const handleMouseUp = () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  }, []);

  const handleClose = () => {
    aiAssistantStore.close();
  };

  return (
    <Drawer
      anchor="right"
      open={aiAssistantStore.isOpen}
      onClose={handleClose}
      PaperProps={{
        sx: {
          width: isMobile ? DRAWER_WIDTH_MOBILE : drawerWidth,
          maxWidth: '100vw',
          top: isMobile ? 0 : contentTop,
          height: isMobile ? '100%' : `calc(100% - ${contentTop}px)`,
        },
      }}
    >
      {/* Resize handle */}
      {!isMobile && (
        <Box
          onMouseDown={handleMouseDown}
          sx={{
            position: 'absolute',
            left: 0,
            top: 0,
            bottom: 0,
            width: 5,
            cursor: 'ew-resize',
            backgroundColor: 'divider',
            zIndex: 100,
            '&:hover': {
              backgroundColor: 'primary.main',
            },
          }}
        />
      )}

      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          ml: isMobile ? 0 : '5px', // Offset for resize handle
        }}
      >
        {/* History with action buttons */}
        <AiAssistantHistory onPrint={handlePrint} />

        <Divider />

        {/* Printable content */}
        <Box ref={printRef} sx={{ display: 'flex', flexDirection: 'column', flex: 1, overflow: 'hidden' }}>
          {/* Chat Area */}
          <AiAssistantChat />
        </Box>

        {/* Input */}
        <AiAssistantInput />
      </Box>
    </Drawer>
  );
});

export default AiAssistantDrawer;
